import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, CloudArrowDown } from "@phosphor-icons/react";

/**
 * Shared "Refresh" control used across pages.
 * Pairs with `useAutoRefresh` — pass it the returned `refresh`, `refreshing`,
 * `lastUpdatedAt`, and (optionally) `livePolling` + `setLivePolling`.
 *
 * Optional `onSync` (e.g. from `useBase44Sync`) renders a primary "Sync"
 * button next to Refresh that pulls fresh data from Base44 first, then
 * refetches. Hide by simply not passing it.
 */
export default function RefreshControl({
  onRefresh,
  refreshing = false,
  lastUpdatedAt = null,
  livePolling = null,
  onToggleLive = null,
  onSync = null,
  syncing = false,
  testId = "refresh-button",
  compact = false,
}) {
  return (
    <div className="flex items-center gap-2" data-testid={`${testId}-wrap`}>
      {!compact && lastUpdatedAt && (
        <span
          className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400"
          data-testid={`${testId}-stamp`}
        >
          Updated <RelativeStamp at={lastUpdatedAt} />
        </span>
      )}

      {onToggleLive && (
        <button
          type="button"
          onClick={() => onToggleLive(!livePolling)}
          data-testid={`${testId}-live`}
          aria-pressed={!!livePolling}
          title={livePolling ? "Stop live polling" : "Auto-refresh every 30s"}
          className={`hidden md:inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border text-[10px] uppercase tracking-widest font-bold transition-colors ${
            livePolling
              ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
              : "border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-zinc-400"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${livePolling ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
          Live
        </button>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={() => onRefresh?.()}
        disabled={refreshing || syncing}
        className="h-9"
        data-testid={testId}
        title="Reload from server cache"
      >
        <ArrowsClockwise
          size={16}
          weight="bold"
          className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`}
        />
        Refresh
      </Button>

      {onSync && (
        <Button
          type="button"
          onClick={() => onSync?.()}
          disabled={syncing || refreshing}
          className="h-9 bg-[#C8102E] hover:bg-[#A00D25] text-white"
          data-testid={`${testId}-sync`}
          title="Pull fresh data from Base44"
        >
          <CloudArrowDown
            size={16}
            weight="bold"
            className={`mr-1.5 ${syncing ? "animate-spin" : ""}`}
          />
          {syncing ? "Syncing…" : "Sync"}
        </Button>
      )}
    </div>
  );
}

function RelativeStamp({ at }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);
  return <>{rel(at)}</>;
}

function rel(at) {
  if (!at) return "just now";
  const diff = Date.now() - new Date(at).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}
